import { useEffect, useState } from "react"
import { motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import { PurchasProvider, usePurchase } from "../../context/purchaseContext"
import { UltimateAlertsProvider } from "../../context/ultimateAlertsContext"
import native from "../../native/native"
import { formatCurrency, formatProductPrice } from "../../utils/utils"
import styles from "./RekindlePaywall.module.scss"

// ── Inline stroke glyphs (fully tint-able, animate cool → warm) ──
type GlyphProps = { className?: string }

const CloudGlyph = ({ className }: GlyphProps) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden>
    <path d="M7.5 18.5h9.2a3.8 3.8 0 0 0 .4-7.57 5.4 5.4 0 0 0-10.3-1.1A3.9 3.9 0 0 0 7.5 18.5Z" />
    <path d="M12 11.4v3.2m-1.4-1.6h2.8" />
  </svg>
)

const FingerprintGlyph = ({ className }: GlyphProps) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden>
    <path d="M6.2 9.2a7 7 0 0 1 11.6 0" />
    <path d="M8.4 11.4a4.4 4.4 0 0 1 7.2 0" />
    <path d="M12 12.2v5.4" />
    <path d="M9.6 13.6c0 2 .2 3 .9 4.2" />
    <path d="M14.4 13.6c0 2.4-.1 3.4-.8 4.4" />
  </svg>
)

const EyeGlyph = ({ className }: GlyphProps) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden>
    <path d="M3 12s3.4-5.6 9-5.6S21 12 21 12s-3.4 5.6-9 5.6S3 12 3 12Z" />
    <circle cx="12" cy="12" r="2.4" />
  </svg>
)

const DecoyGlyph = ({ className }: GlyphProps) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden>
    <rect x="8.5" y="8.5" width="8.5" height="8.5" rx="2" />
    <path d="M14 8.5V7.2a2 2 0 0 0-2-2H7.2a2 2 0 0 0-2 2V12a2 2 0 0 0 2 2h1.3" />
  </svg>
)

const LockGlyph = ({ className }: GlyphProps) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden fill="none">
    <rect
      x="5"
      y="10.5"
      width="14"
      height="10"
      rx="2.4"
      stroke="currentColor"
      strokeWidth={1.9}
    />
    <path
      d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
    />
    <circle cx="12" cy="14.6" r="1.5" fill="currentColor" stroke="none" />
    <path
      d="M12 15v2.4"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
    />
  </svg>
)

const ShieldCheckGlyph = ({ className }: GlyphProps) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden>
    <path d="M12 3 5 5.6v5.1c0 4.3 3 7 7 9.3 4-2.3 7-5 7-9.3V5.6L12 3Z" />
    <path d="M9 11.6l2 2 4-4" />
  </svg>
)

const CheckGlyph = ({ className }: GlyphProps) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden>
    <path
      d="M5 12.5 10 17 19 7"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const BrandLock = ({ className }: GlyphProps) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden fill="none">
    <rect
      x="5"
      y="10.5"
      width="14"
      height="10"
      rx="2.4"
      stroke="currentColor"
      strokeWidth={1.9}
    />
    <path
      d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
    />
  </svg>
)

type Safeguard = {
  name: string
  desc: string
  Glyph: (p: GlyphProps) => JSX.Element
  pos: { top: string; left: string }
}

// N → E → S → W, igniting clockwise as the ring completes
const SAFEGUARDS: Safeguard[] = [
  {
    name: "Encrypted Cloud Vault",
    desc: "Private backup only you can open",
    Glyph: CloudGlyph,
    pos: { top: "8%", left: "50%" },
  },
  {
    name: "Only-You Unlock",
    desc: "Face ID, Touch ID, PIN & pattern",
    Glyph: FingerprintGlyph,
    pos: { top: "50%", left: "92%" },
  },
  {
    name: "Break-In Report",
    desc: "See every failed unlock attempt",
    Glyph: EyeGlyph,
    pos: { top: "92%", left: "50%" },
  },
  {
    name: "Decoy Passcode",
    desc: "A 2nd code opens a harmless vault",
    Glyph: DecoyGlyph,
    pos: { top: "50%", left: "8%" },
  },
]

const R = 96
const CIRC = 2 * Math.PI * R

const RekindleContent = () => {
  const { state: appState } = useAppState()
  const { state: purchaseState, actions: purchaseActions } = usePurchase()
  const reduce = useReducedMotion() ?? false

  const [armed, setArmed] = useState(false)
  const [full, setFull] = useState(false)
  const [selected, setSelected] = useState<"yearly" | "monthly">("yearly")

  const topInset = appState.initialData?.originalTopInset ?? 0
  const bottomInset = appState.initialData?.originalBottomInset ?? 0

  const plan = purchaseState.selectedPlan
  const yearly = plan.yearlyProduct
  const monthly = plan.monthlyProduct

  const yearlyPrice = formatProductPrice(yearly)
  const monthlyPrice = formatProductPrice(monthly)
  const perMonth = formatCurrency(yearly.price / 12, yearly.priceLocale)
  const savings = Math.round((1 - yearly.price / 12 / monthly.price) * 100)
  const trialDays = purchaseState.trialDays
  const yearlyHasTrial = yearly.hasTrial && plan.hasTrial

  // Ignite the ring shortly after mount (or instantly if reduced-motion)
  useEffect(() => {
    if (reduce) {
      setArmed(true)
      setFull(true)
      return
    }
    const t1 = setTimeout(() => setArmed(true), 520)
    const t2 = setTimeout(() => setFull(true), 2050)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
    }
  }, [reduce])

  const replay = () => {
    if (reduce) return
    setArmed(false)
    setFull(false)
    setTimeout(() => setArmed(true), 80)
    setTimeout(() => setFull(true), 1550)
  }

  const buy = () =>
    purchaseActions.purchaseProduct(selected === "yearly" ? yearly : monthly)
  const close = () => native.postNativeEvent("close")
  const restore = () => native.postNativeEvent("already_purchased")
  const terms = () => native.postNativeEvent("terms")
  const privacy = () => native.postNativeEvent("privacy")

  const ctaSub =
    selected === "yearly"
      ? yearlyHasTrial
        ? `${trialDays}-day free trial, then ${yearlyPrice}/yr · cancel anytime`
        : `${yearlyPrice}/yr · cancel anytime`
      : `Billed monthly · ${monthlyPrice}/mo · cancel anytime`

  return (
    <div
      className={`${styles.viewport} ${full ? styles.full : ""}`}
      style={
        {
          "--fc-top": `${topInset}px`,
          "--fc-bottom": `${bottomInset}px`,
        } as React.CSSProperties
      }
    >
      <div className={styles.scroll}>
        <div className={styles.column}>
          {/* top bar */}
          <div className={styles.topBar}>
            <span className={styles.brand}>
              <BrandLock />
              Private Photo Vault&nbsp;<b>· Premium</b>
            </span>
            <button className={styles.notNow} onClick={close}>
              Not now
            </button>
          </div>

          {/* status */}
          <span className={styles.statusPill}>
            <span className={styles.dot} />
            {full ? "Full protection" : "Premium on standby"}
          </span>

          <h1 className={styles.headline}>
            Bring your vault back to <em>full protection</em>
          </h1>
          <p className={styles.subhead}>
            Your photos never left the vault — your passcode still guards them.
            Renew Premium to switch all four safeguards back on.
          </p>

          {/* ring hero */}
          <div className={styles.ring} onClick={replay}>
            <svg className={styles.ringSvg} viewBox="0 0 240 240" aria-hidden>
              <defs>
                <linearGradient id="fcGold" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#ffd489" />
                  <stop offset="55%" stopColor="#ff9d3d" />
                  <stop offset="100%" stopColor="#f2801f" />
                </linearGradient>
              </defs>
              <circle
                className={styles.trackCircle}
                cx="120"
                cy="120"
                r={R}
                strokeWidth={7}
              />
              {/* always-on base protection arc */}
              <circle
                className={styles.baseArc}
                cx="120"
                cy="120"
                r={R}
                strokeWidth={7}
                strokeDasharray={`${CIRC / 6} ${CIRC}`}
              />
              {/* the renewed, full protection ring */}
              <motion.circle
                className={styles.progressCircle}
                cx="120"
                cy="120"
                r={R}
                strokeWidth={7}
                initial={{ pathLength: reduce ? 1 : 0 }}
                animate={{ pathLength: armed ? 1 : reduce ? 1 : 0 }}
                transition={{
                  duration: reduce ? 0 : 1.35,
                  ease: [0.4, 0, 0.2, 1],
                  delay: reduce ? 0 : 0.15,
                }}
              />
            </svg>

            {/* center vault — your private photos, held & sealed */}
            <div className={styles.core}>
              <div className={styles.coreGrid}>
                {[1, 5, 2, 7].map((n) => (
                  <img key={n} src={`/img/lossAversion/photo${n}.png`} alt="" />
                ))}
              </div>
              <div className={styles.coreVeil} />
              <div className={styles.coreSeal} />
              <span className={styles.coreLock}>
                <LockGlyph />
              </span>
            </div>

            {/* safeguard nodes on the ring */}
            {SAFEGUARDS.map((s, i) => (
              <div
                key={s.name}
                className={`${styles.node} ${armed ? styles.on : ""}`}
                style={
                  {
                    top: s.pos.top,
                    left: s.pos.left,
                    "--fc-d": `${0.45 + i * 0.3}s`,
                  } as React.CSSProperties
                }
              >
                <span className={styles.nodeHalo} />
                <span className={styles.nodeDisc}>
                  <s.Glyph className={styles.nodeGlyph} />
                </span>
              </div>
            ))}

            <span className={styles.ringCaption}>
              {full ? "4 of 4 safeguards active" : "Tap to renew the circle"}
            </span>
          </div>

          {/* safeguard legend / reactivation checklist */}
          <div className={styles.legend}>
            {SAFEGUARDS.map((s, i) => (
              <div
                key={s.name}
                className={`${styles.legendItem} ${armed ? styles.on : ""}`}
                style={
                  { "--fc-d": `${0.45 + i * 0.3}s` } as React.CSSProperties
                }
              >
                <span className={styles.legendGlyph}>
                  <s.Glyph />
                </span>
                <span className={styles.legendText}>
                  <span className={styles.legendName}>{s.name}</span>
                  <span className={styles.legendState}>
                    {armed ? "Active" : "Standby"}
                  </span>
                </span>
              </div>
            ))}
          </div>

          {/* pricing — yearly featured */}
          <div className={styles.plans}>
            <button
              className={`${styles.yearly} ${
                selected === "yearly" ? styles.selected : styles.dim
              }`}
              onClick={() => setSelected("yearly")}
            >
              <span className={styles.radio}>
                <CheckGlyph />
              </span>
              <span className={styles.planMain}>
                <span className={styles.planTopLine}>
                  <span className={styles.planTitle}>Yearly</span>
                  <span className={styles.saveBadge}>Save {savings}%</span>
                </span>
                <span className={styles.planSub}>
                  {yearlyHasTrial
                    ? `${trialDays}-day free trial, then ${yearlyPrice}/yr`
                    : "Best value for full protection"}
                </span>
              </span>
              <span className={styles.planPrice}>
                <span className={styles.priceBig}>{yearlyPrice}</span>
                <span className={styles.pricePer}> /yr · {perMonth}/mo</span>
              </span>
            </button>

            <button
              className={`${styles.monthly} ${
                selected === "monthly" ? styles.selected : ""
              }`}
              onClick={() => setSelected("monthly")}
            >
              <span className={styles.monthlyRadio}>
                <CheckGlyph />
              </span>
              Prefer to pay monthly? <b>{monthlyPrice}/mo</b>
            </button>
          </div>

          {/* CTA */}
          <button className={styles.cta} onClick={buy}>
            <span className={styles.ctaSheen} />
            <span className={styles.ctaMain}>Renew Premium Protection</span>
            <span className={styles.ctaSub}>{ctaSub}</span>
          </button>

          <div className={styles.assurance}>
            <ShieldCheckGlyph />
            Your photos &amp; passcode are never affected by Premium status.
          </div>

          <div className={styles.legalRow}>
            <button onClick={restore}>Restore</button>
            <span>·</span>
            <button onClick={terms}>Terms</button>
            <span>·</span>
            <button onClick={privacy}>Privacy</button>
          </div>

          <p className={styles.finePrint}>
            Subscription auto-renews until canceled. Cancel anytime in Settings.
          </p>
        </div>
      </div>
    </div>
  )
}

const RekindlePaywall = () => (
  <PurchasProvider>
    <UltimateAlertsProvider>
      <RekindleContent />
    </UltimateAlertsProvider>
  </PurchasProvider>
)

export default RekindlePaywall
